/* eslint-disable react-hooks/rules-of-hooks */
import React, { useEffect, useState } from 'react';

import { toProjectDateOnly } from 'src/utils/date';
import { useProjectSettingCtx } from 'src/features/projects/contexts/ProjectSettingContext';

import { useParams } from 'react-router-dom';
import { listUnpublishedChanges, publishNewListVersion, deleteDraft, requestDeleteNode } from './api';
// import { utcIsoToDateOnlyKst } from 'src/utils/date';
import { useToast } from 'src/shared/ui/toast/useToast';

type DraftRow = {
    id: string;
    nodeId: string;
    parentNodeId?: string | null;
    code: string;
    name: string;
    level: number;
    plannedStart?: string | null;
    plannedFinish?: string | null;
    weight?: number | null;
    isDeleted?: boolean | null;
    updatedAt?: string;
};

export default function WbsPublishPage(/*{ projectId }: { projectId: string }*/) {
    const toast = useToast();

    const { projectId } = useParams<{ projectId: string }>();

    const { setting } = useProjectSettingCtx();
    const zone = setting?.timeZone ?? 'Asia/Seoul';

    if (!projectId || projectId === '') {
        return <div className="p-6 text-sm text-rose-700">잘못된 프로젝트 ID 입니다.</div>;
    }

    const [rows, setRows] = useState<DraftRow[]>([]);
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const list = await listUnpublishedChanges(projectId);
        setRows(list ?? []);
        setSelected(new Set());
    };
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.nodeId));
    const toggleAll = () => {
        setSelected((prev) => {
            if (allChecked) return new Set();
            return new Set(rows.map((r) => r.nodeId));
        });
    };

    const publishSelected = async () => {
        if (!selected.size) return;
        setBusy(true);
        try {
            await publishNewListVersion(projectId, { selectedNodeIds: Array.from(selected) });
            await load();
            // alert('Published selected changes.');
            toast.success('선택한 변경사항을 게시했습니다.', {
                title: '게시 완료',
            });
        } finally {
            setBusy(false);
        }
    };

    const discardSelectedDrafts = async () => {
        if (!selected.size) return;
        setBusy(true);
        try {
            // 드래프트 폐기
            for (const nodeId of selected) await deleteDraft(projectId, nodeId);
            await load();
            // alert('Discarded selected drafts.');
            toast.success('선택한 드래프트를 폐기했습니다.', {
                title: '처리 완료',
            });
        } finally {
            setBusy(false);
        }
    };

    const requestDeleteSelected = async () => {
        if (!selected.size) return;
        // eslint-disable-next-line no-restricted-globals
        if (!confirm('삭제 요청(다음 발행에 제거) 하시겠습니까?')) return;
        setBusy(true);
        try {
            for (const nodeId of selected) await requestDeleteNode(projectId, nodeId, 'subtree');
            await load();
            // alert('Marked as delete (subtree) for next publish.');
            toast.success('선택 항목을 다음 게시 시 삭제 대상으로 표시했습니다.', {
                title: '삭제 표시 완료',
            });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-3">
            <h2 className="text-lg font-semibold">WBS Draft Changes</h2>

            <div className="flex gap-2">
                <button
                    className="px-3 py-1 rounded bg-indigo-600 text-white disabled:opacity-50"
                    onClick={publishSelected}
                    disabled={!selected.size || busy}
                >
                    Publish Selected
                </button>
                <button
                    className="px-3 py-1 rounded border disabled:opacity-50"
                    onClick={discardSelectedDrafts}
                    disabled={!selected.size || busy}
                >
                    Discard Drafts
                </button>
                <button
                    className="px-3 py-1 rounded border border-red-400 text-red-600 disabled:opacity-50"
                    onClick={requestDeleteSelected}
                    disabled={!selected.size || busy}
                >
                    Mark Delete (subtree)
                </button>
            </div>

            <table className="w-full text-sm border mt-2">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="p-2 border">
                            <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                        </th>
                        <th className="p-2 border">Node</th>
                        <th className="p-2 border">Code</th>
                        <th className="p-2 border">Name</th>
                        <th className="p-2 border">Parent</th>
                        <th className="p-2 border">Dates</th>
                        <th className="p-2 border">Weight</th>
                        <th className="p-2 border">Type</th>
                        <th className="p-2 border">Updated</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => {
                        const isChecked = selected.has(r.nodeId);
                        return (
                            <tr key={r.id}>
                                <td className="p-2 border text-center">
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => {
                                            setSelected((prev) => {
                                                const next = new Set(prev);
                                                if (isChecked) next.delete(r.nodeId);
                                                else next.add(r.nodeId);
                                                return next;
                                            });
                                        }}
                                    />
                                </td>
                                <td className="p-2 border font-mono text-xs">{r.nodeId}</td>
                                <td className="p-2 border">{r.code}</td>
                                <td className="p-2 border">{r.name}</td>
                                <td className="p-2 border font-mono text-xs">{r.parentNodeId ?? '-'}</td>
                                <td className="p-2 border">
                                    {/* {(r.plannedStart ?? '').slice(0, 10)} ~ {(r.plannedFinish ?? '').slice(0, 10)} */}
                                    {/* {utcIsoToDateOnlyKst(r.plannedStart ?? null)} */}
                                    {r.plannedStart ? toProjectDateOnly(r.plannedStart, zone) : ''}
                                </td>
                                <td className="p-2 border">
                                    {r.weight != null ? `${(Number(r.weight) * 100).toFixed(1)}%` : '-'}
                                </td>
                                <td className="p-2 border">
                                    {r.isDeleted ? <span className="text-red-600">DELETE</span> : 'UPSERT'}
                                </td>
                                <td className="p-2 border">
                                    {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '-'}
                                </td>
                            </tr>
                        );
                    })}
                    {!rows.length && (
                        <tr>
                            <td colSpan={9} className="p-4 text-center text-gray-500">
                                No draft changes.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}
